<!--
Performance moment specifically for end of year, which supports
special conversations with connections, uploading documents, and preparation conversations
-->
<template>
    <AddPeopleDialog v-model:dialog="showReviewDialog" />
    <ReviewDocumentUploadDialog
        v-if="currentReview"
        v-model:dialog="showDocumentUploadDialog"
        :review="currentReview"
        @save="handleDocumentDialogComplete"
        @cancel="currentReviewId = null" />

    <!-- Active moment content -->
    <div class="max-w-4xl mx-auto w-full px-0 md:px-4">
        <div class="mt-8 my-6.25">
            <BasePageTitle :title="pageTitle" :subtitle="moment.subtitle" subtitle-multiline />
        </div>

        <NadiaTeamEvaluationsNotification v-if="showCoachNotification" />

        <BaseNotification
            v-if="syncFailed"
            theme="error"
            hide-dismiss
            class="mt-6"
            text="We couldn't update your reviews just now, so what you see may be out of date. Reload the page to try again." />

        <!-- Progress bar -->
        <div v-if="!isLoadingReviews && totalTasks > 0" class="mt-6 mb-2">
            <div class="flex items-center gap-3">
                <div class="grow h-2 rounded-full bg-valence-grey-200 overflow-hidden">
                    <div class="h-full rounded-full bg-valence-primary-500 transition-all duration-500" :style="{ width: `${progressPercent}%` }"></div>
                </div>
                <span class="details text-valence-grey-750 whitespace-nowrap">{{ completedTasks }} of {{ totalTasks }} tasks</span>
            </div>
        </div>
        <ReviewManagerSelectionCard v-if="momentHasOtherCTAs && hasPeopleToReview == null && !isMomentExpired" class="mb-8" :moment-id="moment.id" :moment-title="moment.title" />

        <LoadingSpinner v-if="isLoadingReviews" class="w-10 h-10 mx-auto my-10" />
        <template v-else>
            <!-- Self Evaluation Card -->
            <SelfEvaluationCard
                v-if="shouldShowSelfSection"
                class="mt-8"
                :reviews="selfReviews"
                :due-date="moment.self_moment?.due_date"
                :start-date="moment.self_moment?.start_date"
                :self-title="selfTitle"
                :self-subtitle="moment.self_moment?.subtitle"
                :current-review-id="currentReviewId"
                :is-moment-read-only="isMomentReadOnly"
                @cta-convo-clicked="handleConvoClick"
                @completion-toggled="handleCompletionToggled" />
            <!-- Team evaluation Card -->
            <MyPeopleEvaluationCard
                v-if="shouldShowOthersSection"
                class="mt-8"
                :reviews="otherReviews"
                :due-date="moment.other_moment?.due_date"
                :start-date="moment.other_moment?.start_date"
                :other-title="otherTitle"
                :other-subtitle="moment.other_moment?.subtitle"
                :moment-id="moment.id"
                :current-review-id="currentReviewId"
                :is-moment-read-only="isMomentReadOnly"
                @edit-list="showReviewDialog = true"
                @cta-convo-clicked="handleConvoClick"
                @completion-toggled="handleCompletionToggled" />
            <HasPeopleToReviewSwitcher
                v-if="momentHasOtherCTAs && !isMomentExpired"
                :has-people-to-review="hasPeopleToReview"
                :moment-id="moment.id"
                :moment-title="moment.title" />
        </template>
        <BaseToast v-model:open="isErrorToastOpen" theme="error" :description="errorMessage" />
    </div>
</template>

<script setup lang="ts">
import { usePage } from "@inertiajs/vue3";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import NadiaTeamEvaluationsNotification from "~vue/components/NadiaTeamEvaluationsNotification.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import AddPeopleDialog from "~vue/Moments/AddPeopleDialog.vue";
import { usePerformanceReviewConversations } from "~vue/Moments/composables/usePerformanceReviewConversations";
import { usePerformanceReviewRelationships } from "~vue/Moments/composables/usePerformanceReviewRelationships";
import { usePerformanceReviews } from "~vue/Moments/composables/usePerformanceReviews";
import { usePerformanceReviewSync } from "~vue/Moments/composables/usePerformanceReviewSync";
import { decorateReviews } from "~vue/Moments/composables/useReviewGroupStatus";
import { START_CONVERSATION_ERROR, useStartPerformanceReviewConversation } from "~vue/Moments/composables/useStartPerformanceReviewConversation";
import { PERFORMANCE_REVIEW_EVENT_PREFIX, REVIEW_TYPES } from "~vue/Moments/constants";
import MyPeopleEvaluationCard from "~vue/Moments/MyPeopleEvaluationCard.vue";
import ReviewDocumentUploadDialog from "~vue/Moments/ReviewDocumentUploadDialog.vue";
import ReviewManagerSelectionCard from "~vue/Moments/ReviewManagerSelectionCard.vue";
import SelfEvaluationCard from "~vue/Moments/SelfEvaluationCard.vue";
import type { DecoratedPerformanceReview, PerformanceMoment } from "~vue/types/performance_management";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { computed, onMounted, ref, watch } from "vue";

import HasPeopleToReviewSwitcher from "./HasPeopleToReviewSwitcher.vue";

const { moment, hasPeopleToReview, showCoachNotification } = defineProps<{
    moment: PerformanceMoment;
    hasPeopleToReview: boolean | null;
    showCoachNotification?: boolean;
}>();

const pageTitle = moment.visibility === "preview" ? `${moment.title} (preview)` : moment.title;

const isLoadingReviews = ref(true);

const selfReviews = computed(() => (reviews.value ?? []).filter((review) => review.cta?.reviewer_target == "self"));
const otherReviews = computed(() => (reviews.value ?? []).filter((review) => review.cta?.reviewer_target == "connection"));
const momentHasOtherCTAs = computed(() => moment.ctas.some((cta) => cta.reviewer_target === "connection"));
const isMomentExpired = computed(() => moment.state === "expired");
const shouldShowSelfSection = computed(() => !!selfReviews.value.length);
const shouldShowOthersSection = computed(() => hasPeopleToReview && momentHasOtherCTAs.value);

const selfTitle = computed(() => {
    if (moment.self_moment?.title) {
        return moment.self_moment.title;
    }
    return shouldShowOthersSection.value ? "Myself" : "";
});
const otherTitle = computed(() => {
    if (moment.other_moment?.title) {
        return moment.other_moment.title;
    }
    return shouldShowSelfSection.value ? "My Team" : "";
});

const { data: performanceReviewRelationships, execute: fetchPerformanceReviewRelationships } = usePerformanceReviewRelationships();
const { data: performanceReviewsData, execute: fetchPerformanceReviews } = usePerformanceReviews(moment.id);
const { data: conversationsData, execute: fetchConversations } = usePerformanceReviewConversations(moment.id);
const { sync: syncPerformanceReviews, error: syncError, statusCode: syncStatusCode } = usePerformanceReviewSync();

// Flag snapshot sent by get_performance_management_props, logged with moment_viewed so a dead
// or half-rendered page can be diagnosed from analytics alone.
const pageFeatureFlags = (usePage().props.page_feature_flags ?? {}) as Record<string, boolean>;

const syncFailed = ref(false);

const handleMountSyncFailure = (error: unknown) => {
    // The session-ended dialog owns that messaging; a banner behind it would contradict it.
    if (isSessionEndedError(error)) {
        return;
    }
    logError(error ?? new Error("Performance review sync failed on mount"), {
        tags: { feature: "performance_management", action: "mount_sync" },
        context: { moment_id: moment.id, http_status: syncStatusCode.value ?? null },
    });
    syncFailed.value = true;
};

// Sync on mount, then fetch relationships, reviews, and conversations
onMounted(async () => {
    let syncSucceeded = false;
    try {
        await syncPerformanceReviews(moment.id);
        syncSucceeded = !syncError.value;
        if (!syncSucceeded) {
            handleMountSyncFailure(syncError.value);
        }
    } catch (error) {
        handleMountSyncFailure(error);
    }

    // Run even when the sync failed: these are GETs and can still succeed, and a page with
    // slightly stale reviews beats the dead shell users hit when a sync 403 aborted everything.
    try {
        await Promise.all([fetchPerformanceReviewRelationships(), fetchPerformanceReviews(), fetchConversations()]);
    } finally {
        isLoadingReviews.value = false;
    }

    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_MOMENT_VIEWED, {
        moment_id: moment.id,
        review_count: reviews.value.length,
        sync_succeeded: syncSucceeded,
        ...pageFeatureFlags,
    });
});

const showReviewDialog = ref(false);
watch(
    () => hasPeopleToReview,
    (newValue, oldValue) => {
        // Open the dialog when the user transitions into needing to pick connections:
        // flipping the switcher on (false/null → true) with no team members yet, so they can add some
        if (!oldValue && newValue && otherReviews.value.length === 0) {
            showReviewDialog.value = true;
        }
    },
);
const showDocumentUploadDialog = ref(false);

// Refetch relationships, reviews, and conversations when dialog closes
watch(showReviewDialog, async (isOpen, wasOpen) => {
    if (wasOpen && !isOpen) {
        await syncPerformanceReviews(moment.id);
        await Promise.all([fetchPerformanceReviewRelationships(), fetchPerformanceReviews(), fetchConversations()]);
    }

    if (isOpen && !wasOpen) {
        logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}edit_team_list_opened`, { moment_id: moment.id });
    }
});

const reviews = computed<Array<DecoratedPerformanceReview>>(() =>
    decorateReviews(
        performanceReviewsData.value?.reviews || [],
        moment.ctas,
        performanceReviewRelationships.value?.relationships || [],
        conversationsData.value?.conversations || [],
    ),
);

const currentReviewId = ref<string | null>(null);
const currentReview = computed(() => {
    if (!currentReviewId.value) {
        return null;
    }
    return reviews.value.find((r) => r.id === currentReviewId.value) || null;
});

const { launchError, startConversation } = useStartPerformanceReviewConversation();

const isErrorToastOpen = ref(false);
const errorMessage = ref("");

const showLaunchError = (message: string) => {
    errorMessage.value = message;
    isErrorToastOpen.value = true;
};

const startPerformanceReviewConversation = async (review: DecoratedPerformanceReview) => {
    try {
        const url = await startConversation(review);
        if (url) {
            openUrl(url);
        } else if (launchError.value) {
            showLaunchError(launchError.value);
        }
    } finally {
        currentReviewId.value = null;
    }
};

const handleConvoClick = async (reviewId: string) => {
    const review = reviews.value.find((r) => r.id === reviewId);
    if (!review) {
        return;
    }
    currentReviewId.value = reviewId;

    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}convo_clicked`, {
        moment_id: moment.id,
        review_id: reviewId,
        cta_id: review.performance_moment_cta_id,
        prompt_call_code: review.cta?.prompt_call_code,
        review_for: !review.relationship ? REVIEW_TYPES.SELF : REVIEW_TYPES.CONNECTIONS,
    });

    if (!review.conversation) {
        if (review.cta?.uploads_enabled) {
            showDocumentUploadDialog.value = true;
            logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PRE_CONVO_UPLOAD_DIALOG_VIEWED, {
                moment_id: moment.id,
                review_id: reviewId,
                cta_id: review.performance_moment_cta_id,
                prompt_call_code: review.cta?.prompt_call_code,
                review_for: !review.relationship ? REVIEW_TYPES.SELF : REVIEW_TYPES.CONNECTIONS,
            });
        } else {
            await startPerformanceReviewConversation(review);
        }
    } else if (review.conversation.coaching_session_url) {
        openUrl(review.conversation.coaching_session_url);
    } else {
        // The list API excludes sessionless conversations, so a missing URL means stale data —
        // navigating anywhere would just reload the page, so surface the failure instead.
        showLaunchError(START_CONVERSATION_ERROR);
        currentReviewId.value = null;
    }
};

const handleDocumentDialogComplete = async (uploadedDocumentCount: number) => {
    // After documents are uploaded or skipped, start the conversation
    if (currentReview.value) {
        logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}pre_convo_upload_dialog_closed`, {
            uploaded_document_count: uploadedDocumentCount,
            moment_id: moment.id,
            review_id: currentReview.value.id,
            cta_id: currentReview.value.performance_moment_cta_id,
            prompt_call_code: currentReview.value.cta?.prompt_call_code,
            review_for: !currentReview.value.relationship ? REVIEW_TYPES.SELF : REVIEW_TYPES.CONNECTIONS,
        });

        await startPerformanceReviewConversation(currentReview.value);
    }
};

const isMomentReadOnly = computed(() => moment.state === "expired" || moment.state === "locked");

const totalTasks = computed(() => reviews.value.length);
const completedTasks = computed(() => reviews.value.filter((r) => !!r.manually_completed_at || r.conversation?.state === "completed").length);
const progressPercent = computed(() => (totalTasks.value > 0 ? Math.round((completedTasks.value / totalTasks.value) * 100) : 0));

const handleCompletionToggled = async () => {
    await fetchPerformanceReviews();
};
</script>
